import React, { useMemo, useState, useEffect, useRef } from 'react';
import '../../../styles/newsletter-signup.css';
import { clampPercent } from '../utils/format';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import ShareModal from '../../courseShare/components/ShareModal';
import { useSearchCourses } from '../hooks/useSearchCourses';
import SearchResults from './SearchResults';
import { useAuth } from '../../../auth/AuthProvider';
import { greeting } from '../../../utils/greeting';
import { PageContainer, PageTitle } from '../../../components/PageHeader';
import CourseCard from './CourseCard';
import ResumePanel from './ResumePanel';
import { Skeleton, SkeletonPanel, SkeletonRegion } from '../../../components/ui/Skeleton';
import './dashboard.css';

/**
 * Icons are Heroicons (outline, https://heroicons.com), drawn on a 24x24
 * grid at their native stroke-width of 1.5 — see
 * .superpowers/sdd/2026-08-10-visual-redesign-shell-dashboard/icons-heroicons.md
 * for the "Create course" source path. Chevron-down is sourced the same way
 * to match the rest of the set.
 */
function ChevronDownIcon(props) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
    </svg>
  );
}

function PlusCircleIcon(props) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v6m3-3H9m12 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
    </svg>
  );
}

/**
 * Props:
 * - courses, status, loading
 * - onRefresh(), onLoadAll(), onToggleBookmark(courseId, nextVal), onDelete(courseId)
 * - firstName (string)
 * - pagination: { totalCount, totalPages, currentPage, limit, goToPage, nextPage, prevPage, changePageSize }
 */
export default function DashboardView({
  courses,
  continueLearning,
  status,
  loading,
  onRefresh,
  onToggleBookmark,
  onDelete,
  firstName,
  pagination,
  onLoadAll, // New prop to fetch all courses
}) {
  const [query, setQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const searchContainerRef = useRef(null);

  // Get preferences from auth context
  const { preferences, updatePreferences } = useAuth();

  // SSE-based search
  const {
    results: searchResults,
    isSearching,
    error: searchError,
    totalResults,
    clearSearch,
  } = useSearchCourses(query, { debounceMs: 300, limit: 15 });

  // Map API format (CARD/LIST) to UI format (cards/list)
  const apiToUiViewMode = (apiValue) => apiValue === 'LIST' ? 'list' : 'cards';
  const uiToApiViewMode = (uiValue) => uiValue === 'list' ? 'LIST' : 'CARD';

  // Initialize viewMode from preferences
  const [viewMode, setViewModeLocal] = useState(() => {
    // First check preferences, then localStorage as fallback
    if (preferences?.dashboardLayout) {
      return apiToUiViewMode(preferences.dashboardLayout);
    }
    try { return localStorage.getItem('dashboard.viewMode') || 'cards'; }
    catch { return 'cards'; }
  }); // 'cards' | 'list'

  // Sync viewMode with preferences when they load
  useEffect(() => {
    if (preferences?.dashboardLayout) {
      const preferredViewMode = apiToUiViewMode(preferences.dashboardLayout);
      if (preferredViewMode !== viewMode) {
        setViewModeLocal(preferredViewMode);
      }
    }
    // viewMode is intentionally omitted: this effect sets it based on
    // preferences, so including it would re-fire whenever it changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferences?.dashboardLayout]);

  // Handle viewMode change - update both local state and preferences
  const setViewMode = async (newMode) => {
    setViewModeLocal(newMode);
    try {
      await updatePreferences({ dashboardLayout: uiToApiViewMode(newMode) });
    } catch (e) {
      console.error('[DashboardView] Failed to save view mode preference:', e);
    }
  };

  const [sortMode, setSortMode] = useState(() => {
    try {
      const v = localStorage.getItem('dashboard.sortMode') || 'recentlyAccessed';
      const allowed = new Set(['recentlyAccessed', 'newestFirst', 'oldestFirst', 'progressAsc', 'progressDesc', 'alphaAsc', 'alphaDesc', 'bookmarked']);
      // Migrate old 'all' value to 'recentlyAccessed'
      if (v === 'all') return 'recentlyAccessed';
      return allowed.has(v) ? v : 'recentlyAccessed';
    } catch {
      return 'recentlyAccessed';
    }
  });
  useEffect(() => {
    try { localStorage.setItem('dashboard.viewMode', viewMode); } catch { }
  }, [viewMode]);
  useEffect(() => {
    try { localStorage.setItem('dashboard.sortMode', sortMode); } catch { }
  }, [sortMode]);

  // Show search results dropdown when query is present
  useEffect(() => {
    if (query.trim()) {
      setShowSearchResults(true);
    }
  }, [query]);

  // Close search dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load all courses when viewing bookmarked only
  const needsAllCourses = sortMode === 'bookmarked';
  useEffect(() => {
    if (needsAllCourses && onLoadAll) {
      onLoadAll();
    } else if (!needsAllCourses && onRefresh) {
      // Return to paginated view
      onRefresh();
    }
  }, [needsAllCourses, onLoadAll, onRefresh]);
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [shareCourse, setShareCourse] = useState(null);
  const [bookmarkOverrides, setBookmarkOverrides] = useState(() => {
    try { return JSON.parse(localStorage.getItem('bookmarkOverrides') || '{}'); }
    catch { return {}; }
  });
  useEffect(() => {
    localStorage.setItem('bookmarkOverrides', JSON.stringify(bookmarkOverrides));
  }, [bookmarkOverrides]);
  useEffect(() => {
    if (!Array.isArray(courses)) return;
    setBookmarkOverrides((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const c of courses) {
        const ov = next[c.courseId];
        if (ov === undefined) continue;
        if (typeof c.bookmarked === 'boolean' && c.bookmarked === ov) {
          delete next[c.courseId];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [courses]);
  const isBookmarkedEffective = (c) => {
    const ov = bookmarkOverrides?.[c.courseId];
    return typeof ov === 'boolean' ? ov : !!c.bookmarked;
  };
  const navigate = useNavigate();

  useEffect(() => {
    const onDocDown = () => { setMenuOpenId(null); setSortMenuOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') { setMenuOpenId(null); setSortMenuOpen(false); } };
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  const sortLabel = useMemo(() => {
    switch (sortMode) {
      case 'recentlyAccessed': return 'Recently Accessed';
      case 'newestFirst': return 'Newest';
      case 'oldestFirst': return 'Oldest';
      case 'progressAsc': return 'Progress ↑';
      case 'progressDesc': return 'Progress ↓';
      case 'alphaAsc': return 'A → Z';
      case 'alphaDesc': return 'Z → A';
      case 'bookmarked': return 'Bookmarked';
      default: return 'Recently Accessed';
    }
  }, [sortMode]);

  const visibleCourses = useMemo(() => {
    // Search is now handled by SSE - this only handles sorting/filtering for display
    const base = courses || [];

    let list = sortMode === 'bookmarked' ? base.filter((c) => isBookmarkedEffective(c)) : base.slice();
    const tuples = list.map((c, i) => ({ c, i }));
    const pct = (x) => clampPercent(x?.completionPercentage);
    const getTime = (x) => new Date(x?.createdAt || 0).getTime();
    const getLastAccessed = (x) => x?.lastAccessedAt ? new Date(x.lastAccessedAt).getTime() : 0;

    if (sortMode === 'recentlyAccessed') {
      // Sort by lastAccessedAt descending, fall back to createdAt
      tuples.sort((a, b) => {
        const aTime = getLastAccessed(a.c) || getTime(a.c);
        const bTime = getLastAccessed(b.c) || getTime(b.c);
        return bTime - aTime || a.i - b.i;
      });
    }
    else if (sortMode === 'newestFirst') tuples.sort((a, b) => getTime(b.c) - getTime(a.c) || a.i - b.i);
    else if (sortMode === 'oldestFirst') tuples.sort((a, b) => getTime(a.c) - getTime(b.c) || a.i - b.i);
    else if (sortMode === 'progressAsc') tuples.sort((a, b) => pct(a.c) - pct(b.c) || a.i - b.i);
    else if (sortMode === 'progressDesc') tuples.sort((a, b) => pct(b.c) - pct(a.c) || a.i - b.i);
    else if (sortMode === 'alphaAsc') tuples.sort((a, b) => String(a.c.name || '').localeCompare(String(b.c.name || '')) || a.i - b.i);
    else if (sortMode === 'alphaDesc') tuples.sort((a, b) => String(b.c.name || '').localeCompare(String(a.c.name || '')) || a.i - b.i);

    return tuples.map((t) => t.c);
    // isBookmarkedEffective is computed from bookmarkOverrides which is
    // already a dep; adding the helper would not change behavior.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courses, sortMode, bookmarkOverrides]);

  // Continue learning is now derived server-side and passed as a prop
  const continueLearnCourse = continueLearning;

  return (
    <div className="dashboard-scope">
      <PageContainer>
          {/* Header */}
          <header style={{ marginBottom: 20 }}>
            <PageTitle style={{ marginBottom: 12 }}>
              {greeting(firstName)}
            </PageTitle>

            {!continueLearnCourse && (
              <p className="title" style={{ color: 'var(--text-secondary)', fontWeight: 500, fontSize: 'clamp(14px,3.5vw,18px)', marginBottom: 0 }}>
                Your courses, cleanly organized.
              </p>
            )}
          </header>

          {/* Resume panel — both actions currently land on the same route;
              there is no separate outline view in this app. */}
          <ResumePanel
            course={continueLearnCourse}
            onContinue={() => {
              try { sessionStorage.setItem('ui.currentCourseTitle', continueLearnCourse.name || ''); } catch { }
              navigate(`/courses/${continueLearnCourse.courseId}`);
            }}
            onViewOutline={() => {
              try { sessionStorage.setItem('ui.currentCourseTitle', continueLearnCourse.name || ''); } catch { }
              navigate(`/courses/${continueLearnCourse.courseId}`);
            }}
          />

          {/* Toolbar — search · Cards/List segmented · sort · spacer · Create course */}
          <div className="dash-toolbar">
            {/* Search input with dropdown */}
            <div
              ref={searchContainerRef}
              className="dash-search search-container"
              style={{ position: 'relative', zIndex: 50 }}
            >
              <div className="signup-card search-bar-card"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '8px 12px',
                    border: '1px solid var(--border-subtle)',
                    borderRadius: 12,
                    minHeight: 44,
                    background: 'var(--bg-primary)',
                  }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true"
                    style={{ opacity: 0.9 }}
                    fill="none" stroke="currentColor" strokeWidth="1.8"
                    strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="7" />
                    <path d="M20 20l-3.2-3.2" />
                  </svg>
                  <input
                    className="hint"
                    placeholder="Search courses…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onFocus={() => query.trim() && setShowSearchResults(true)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        setShowSearchResults(false);
                        e.target.blur();
                      }
                    }}
                    style={{
                      flex: 1,
                      background: 'transparent',
                      border: 0,
                      outline: 'none',
                      color: 'var(--text-primary)',
                      fontSize: 'clamp(14px,3.2vw,15px)',
                      padding: 0,
                      lineHeight: 1.2,
                    }}
                  />
                  {/* Clear button when there's a query */}
                  {query && (
                    <button
                      type="button"
                      onClick={() => {
                        setQuery('');
                        clearSearch();
                        setShowSearchResults(false);
                      }}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 4,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0.6,
                      }}
                      aria-label="Clear search"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 6L6 18M6 6l12 12" />
                      </svg>
                    </button>
                  )}

                  {/* Mobile-only sort button integrated in search bar */}
                  <div className={`mobile-sort-inline menu-anchor ${sortMenuOpen ? 'open' : ''}`}>
                    <button
                      type="button"
                      className="mobile-sort-btn"
                      aria-haspopup="menu"
                      aria-expanded={sortMenuOpen ? 'true' : 'false'}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuOpenId(null);
                        setSortMenuOpen((v) => !v);
                      }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 6h18M6 12h12M9 18h6" />
                      </svg>
                    </button>
                    {sortMenuOpen && (
                      <div
                        className="menu-card mobile-sort-menu"
                        role="menu"
                        aria-label="Sort options"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button className={`menu-item ${sortMode === 'recentlyAccessed' ? 'is-active' : ''}`} role="menuitem" onClick={() => { setSortMode('recentlyAccessed'); setSortMenuOpen(false); }}>Recently Accessed</button>
                        <button className={`menu-item ${sortMode === 'newestFirst' ? 'is-active' : ''}`} role="menuitem" onClick={() => { setSortMode('newestFirst'); setSortMenuOpen(false); }}>Newest</button>
                        <button className={`menu-item ${sortMode === 'oldestFirst' ? 'is-active' : ''}`} role="menuitem" onClick={() => { setSortMode('oldestFirst'); setSortMenuOpen(false); }}>Oldest</button>
                        <div className="menu-sep" />
                        <button className={`menu-item ${sortMode === 'progressAsc' ? 'is-active' : ''}`} role="menuitem" onClick={() => { setSortMode('progressAsc'); setSortMenuOpen(false); }}>
                          Progress ↑
                        </button>
                        <button className={`menu-item ${sortMode === 'progressDesc' ? 'is-active' : ''}`} role="menuitem" onClick={() => { setSortMode('progressDesc'); setSortMenuOpen(false); }}>
                          Progress ↓
                        </button>
                        <div className="menu-sep" />
                        <button className={`menu-item ${sortMode === 'alphaAsc' ? 'is-active' : ''}`} role="menuitem" onClick={() => { setSortMode('alphaAsc'); setSortMenuOpen(false); }}>A → Z</button>
                        <button className={`menu-item ${sortMode === 'alphaDesc' ? 'is-active' : ''}`} role="menuitem" onClick={() => { setSortMode('alphaDesc'); setSortMenuOpen(false); }}>Z → A</button>
                        <div className="menu-sep" />
                        <button className={`menu-item ${sortMode === 'bookmarked' ? 'is-active' : ''}`} role="menuitem" onClick={() => { setSortMode(sortMode === 'bookmarked' ? 'recentlyAccessed' : 'bookmarked'); setSortMenuOpen(false); }}>
                          {sortMode === 'bookmarked' ? 'Show All' : 'Bookmarked'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Search results dropdown */}
                {showSearchResults && (
                  <SearchResults
                    results={searchResults}
                    isSearching={isSearching}
                    error={searchError}
                    query={query}
                    totalResults={totalResults}
                    onClose={() => {
                      setShowSearchResults(false);
                      setQuery('');
                      clearSearch();
                    }}
                  />
                )}
              </div>

            {/* Cards/List segmented control - hidden on mobile */}
            <div className="dash-segmented desktop-view-sort-controls">
              <button
                type="button"
                className={viewMode === 'cards' ? 'is-active' : ''}
                onClick={() => setViewMode('cards')}
              >
                Cards
              </button>
              <button
                type="button"
                className={viewMode === 'list' ? 'is-active' : ''}
                onClick={() => setViewMode('list')}
              >
                List
              </button>
            </div>

            {/* Sort dropdown - hidden on mobile */}
            <div className={`menu-anchor desktop-view-sort-controls ${sortMenuOpen ? 'open' : ''}`}>
              <button
                type="button"
                className="dash-sort"
                aria-haspopup="menu"
                aria-expanded={sortMenuOpen ? 'true' : 'false'}
                aria-controls="sort-menu"
                onMouseDown={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    setMenuOpenId(null);
                    setSortMenuOpen((v) => !v);
                  }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuOpenId(null);            // close any open kebab
                  setSortMenuOpen((v) => !v);     // toggle sort menu
                }}
              >
                <span>Sort: {sortLabel}</span>
                <ChevronDownIcon aria-hidden="true" />
              </button>
                {sortMenuOpen && (
                  <div
                    id="sort-menu"
                    className="menu-card"
                    role="menu"
                    aria-label="Sort options"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button className="menu-item" role="menuitem" onClick={() => { setSortMode('recentlyAccessed'); setSortMenuOpen(false); }}>Recently Accessed</button>
                    <button className="menu-item" role="menuitem" onClick={() => { setSortMode('newestFirst'); setSortMenuOpen(false); }}>Newest</button>
                    <button className="menu-item" role="menuitem" onClick={() => { setSortMode('oldestFirst'); setSortMenuOpen(false); }}>Oldest</button>
                    <div className="menu-sep" />
                    <button className="menu-item" role="menuitem" onClick={() => { setSortMode('progressAsc'); setSortMenuOpen(false); }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        Progress <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7 14l5-5 5 5" /></svg>
                      </span>
                    </button>
                    <button className="menu-item" role="menuitem" onClick={() => { setSortMode('progressDesc'); setSortMenuOpen(false); }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        Progress <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7 10l5 5 5-5" /></svg>
                      </span>
                    </button>
                    <div className="menu-sep" />
                    <button className="menu-item" role="menuitem" onClick={() => { setSortMode('alphaAsc'); setSortMenuOpen(false); }}>A → Z</button>
                    <button className="menu-item" role="menuitem" onClick={() => { setSortMode('alphaDesc'); setSortMenuOpen(false); }}>Z → A</button>
                    <div className="menu-sep" />
                    <button className="menu-item" role="menuitem" onClick={() => { setSortMode(sortMode === 'bookmarked' ? 'recentlyAccessed' : 'bookmarked'); setSortMenuOpen(false); }}>
                      {sortMode === 'bookmarked' ? 'Show All' : 'Bookmarked'}
                    </button>
                  </div>
                )}
            </div>

            <div className="dash-toolbar-spacer" />

            {/* Create course - hidden on mobile (FAB takes over) */}
            <div className="desktop-only-create">
              <button type="button" className="dash-create" onClick={() => navigate('/course/new')}>
                <PlusCircleIcon aria-hidden="true" />
                Create course
              </button>
            </div>
          </div>

          {/* Status */}
          {status && <p className="hint" aria-live="polite" style={{ marginBottom: 12 }}>{status}</p>}

          {/* Content.
              `loading` is gated on there being nothing to show yet, not on
              the fetch alone: a refetch or a page change re-raises `loading`
              while the previous page is still on screen, and swapping live
              cards for placeholders there would be a flash backwards. */}
          {loading && visibleCourses.length === 0 ? (
            <CoursesSkeleton viewMode={viewMode} />
          ) : viewMode === 'list' ? (
            <ListView
              visibleCourses={visibleCourses}
              onToggleBookmark={onToggleBookmark}
              bookmarkOverrides={bookmarkOverrides}
              setBookmarkOverrides={setBookmarkOverrides}
              menuOpenId={menuOpenId}
              setMenuOpenId={setMenuOpenId}
              setSortMenuOpen={setSortMenuOpen}
              onAskDelete={(id) => setConfirmDeleteId(id)}
              onShare={(course) => setShareCourse(course)}
            />
          ) : (
            <CardsView
              visibleCourses={visibleCourses}
              menuOpenId={menuOpenId}
              setMenuOpenId={setMenuOpenId}
              onToggleBookmark={onToggleBookmark}
              onAskDelete={(id) => setConfirmDeleteId(id)}
              setSortMenuOpen={setSortMenuOpen}
              bookmarkOverrides={bookmarkOverrides}
              setBookmarkOverrides={setBookmarkOverrides}
              onShare={(course) => setShareCourse(course)}
            />
          )}

          {/* Pagination Controls - only show when not viewing bookmarked filter */}
          {pagination && pagination.totalCount > 0 && sortMode !== 'bookmarked' && (
            <PaginationControls
              pagination={pagination}
              loading={loading}
            />
          )}
      </PageContainer>

      {/* Mobile Create Course FAB */}
      <button
        className="mobile-create-fab"
        onClick={() => navigate('/course/new')}
        aria-label="Start new course"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 5v14M5 12h14" />
        </svg>
      </button>

      {/* Confirmation Modal */}
      {confirmDeleteId && (
        <div
          className="modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-title"
          onClick={() => setConfirmDeleteId(null)}
          onKeyDown={(e) => { if (e.key === 'Escape') setConfirmDeleteId(null); }}
        >
          <div
            className="modal-card"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="delete-title" className="title" style={{ fontSize: 18, marginBottom: 8 }}>
              Are you sure that you want to delete this course?
            </h3>
            <p className="hint">This action will remove the course from your dashboard. You can't undo this.</p>
            <div className="modal-actions">
              <button className="secondary-button" onClick={() => setConfirmDeleteId(null)}>Deny</button>
              <button
                className="cta-button"
                style={{ background: 'var(--danger)' }}
                onClick={async () => {
                  const id = confirmDeleteId;
                  setConfirmDeleteId(null);
                  await onDelete(id);
                  await onRefresh();
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Course Modal. Keyed on courseId: ShareModal stays mounted across
          different courses (only `open`/`courseId` change), and its
          useCreateShare hook never resets its own status back to 'idle' after
          the first mint — without a key change here it would keep showing the
          previous course's link. The key forces a fresh mount (and hook state)
          per course. */}
      <ShareModal
        key={shareCourse?.courseId || 'none'}
        open={!!shareCourse}
        onClose={() => setShareCourse(null)}
        courseId={shareCourse?.courseId}
        courseTitle={shareCourse?.name}
      />

      {/* Mobile-only styles */}
      <style>{`
        /* Hide desktop create button on mobile */
        @media (max-width: 768px) {
          .desktop-only-create {
            display: none !important;
          }
        }

        /* Mobile Create FAB - hidden on desktop */
        .mobile-create-fab {
          display: none;
        }

        /* Mobile sort button in search bar - hidden on desktop */
        .mobile-sort-inline {
          display: none;
        }


        @media (max-width: 768px) {
          .mobile-create-fab {
            display: flex;
            align-items: center;
            justify-content: center;
            position: fixed;
            bottom: 24px;
            right: 24px;
            width: 56px;
            height: 56px;
            border-radius: 16px;
            background: var(--bg-tertiary);
            border: none;
            color: var(--text-primary);
            cursor: pointer;
            z-index: 90;
            box-shadow: var(--shadow-md);
            transition: transform 0.2s ease, box-shadow 0.2s ease;
          }

          .mobile-create-fab:active {
            transform: scale(0.95);
          }

          /* Hide entire desktop view/sort controls on mobile */
          .desktop-view-sort-controls {
            display: none !important;
          }

          /* Show mobile sort button in search bar */
          .mobile-sort-inline {
            display: flex;
            position: relative;
          }

          .mobile-sort-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 36px;
            height: 36px;
            border-radius: 8px;
            background: var(--glass-bg-medium);
            border: 1px solid var(--border-subtle);
            color: var(--text-secondary);
            cursor: pointer;
            transition: all 0.15s ease;
          }

          .mobile-sort-btn:active {
            background: var(--glass-bg-light);
            transform: scale(0.96);
          }

          .mobile-sort-menu {
            position: absolute;
            top: calc(100% + 8px);
            right: 0;
            min-width: 160px;
            z-index: 60;
            box-shadow: var(--shadow-lg);
          }

          .mobile-sort-menu .menu-item.is-active {
            background: var(--accent-light);
            color: var(--accent-primary);
          }

          /* Keep search container below the topbar (z-index: 100) so it scrolls behind it */
          .search-container {
            z-index: 50 !important;
          }

          /* Force cards to have lower stacking context */
          .cards-grid,
          .list-wrap {
            position: relative;
            z-index: 1;
          }

          .cards-grid .signup-card,
          .list-wrap .signup-card {
            z-index: 1 !important;
          }

          /* Disable hover transforms on touch devices to prevent scroll jank */
          .dashboard-scope .signup-card.search-bar-card {
            transform: none !important;
          }

          .dashboard-scope .signup-card.search-bar-card:hover {
            transform: none !important;
          }
        }

        /* Touch device specific - disable hover transforms during scroll */
        @media (hover: none) and (pointer: coarse) {
          .dashboard-scope .signup-card.search-bar-card {
            transform: none !important;
            transition: border-color 0.2s ease, background 0.2s ease;
          }

          .dashboard-scope .signup-card.search-bar-card:hover,
          .dashboard-scope .signup-card.search-bar-card:active {
            transform: none !important;
          }
        }
      `}</style>
    </div>
  );
}

// --- Internal feature-presentational parts (exact same visuals) ---

/**
 * Stand-in for the course grid/list on a cold load.
 *
 * Before this existed the dashboard had no loading branch at all: during the
 * initial fetch `visibleCourses` is empty, so CardsView/ListView fell through
 * to their "No courses match your filters." empty state and told a returning
 * user with a full library that they had nothing. That empty state is now
 * only reachable once the fetch has actually answered.
 *
 * Reuses .cards-grid / .list-wrap so the placeholders occupy the same tracks
 * the real cards will.
 */
function CoursesSkeleton({ viewMode }) {
  const isList = viewMode === 'list';

  return (
    <SkeletonRegion label="Loading your courses">
      <div className={isList ? 'list-wrap' : 'cards-grid'} style={isList ? { display: 'grid', gap: 16 } : undefined}>
        {Array.from({ length: isList ? 5 : 8 }, (_, i) => (
          <SkeletonPanel key={i} style={isList ? { display: 'flex', alignItems: 'center', gap: 'var(--space-4)' } : undefined}>
            {isList ? (
              <>
                <Skeleton width={56} height={56} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Skeleton width="46%" height={16} style={{ marginBottom: 'var(--space-2)' }} />
                  <Skeleton width="28%" height={12} />
                </div>
                <Skeleton width={90} height={6} />
              </>
            ) : (
              <>
                <Skeleton height={104} style={{ marginBottom: 'var(--space-3)' }} />
                <Skeleton width="72%" height={16} style={{ marginBottom: 'var(--space-2)' }} />
                <Skeleton width="44%" height={12} style={{ marginBottom: 'var(--space-4)' }} />
                <Skeleton height={6} />
              </>
            )}
          </SkeletonPanel>
        ))}
      </div>
    </SkeletonRegion>
  );
}

function ListView({ visibleCourses, menuOpenId, setMenuOpenId, onToggleBookmark, onAskDelete, setSortMenuOpen, bookmarkOverrides, setBookmarkOverrides, onShare }) {
  const navigate = useNavigate();

  const openCourse = (c) => {
    try { sessionStorage.setItem('ui.currentCourseTitle', c.name || ''); } catch { }
    navigate(`/courses/${c.courseId}`);
  };

  if (visibleCourses.length === 0) {
    return (
      <div className="list-wrap" style={{ display: 'grid', gap: 16 }}>
        <div className="signup-card" style={{ border: '1px solid var(--border-subtle)' }}>
          <p className="hint" style={{ textAlign: 'center' }}>No courses match your filters.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="list-wrap" style={{ display: 'grid', gap: 16 }}>
      {visibleCourses.map((c) => {
        const eff = (bookmarkOverrides && typeof bookmarkOverrides[c.courseId] === 'boolean')
          ? bookmarkOverrides[c.courseId]
          : !!c.bookmarked;
        return (
          <CourseCard
            key={c.courseId}
            course={c}
            variant="row"
            bookmarked={eff}
            menuOpen={menuOpenId === c.courseId}
            onMenuOpenChange={(next) => { setSortMenuOpen(false); setMenuOpenId(next ? c.courseId : null); }}
            onOpen={() => openCourse(c)}
            onToggleBookmark={() => {
              const next = !eff;
              setBookmarkOverrides((prev) => ({ ...(prev || {}), [c.courseId]: next }));
              onToggleBookmark(c.courseId, next);
            }}
            onShare={() => { setMenuOpenId(null); onShare(c); }}
            onDelete={() => { setMenuOpenId(null); onAskDelete(c.courseId); }}
          />
        );
      })}
    </div>
  );
}

function CardsView({ visibleCourses, menuOpenId, setMenuOpenId, onToggleBookmark, onAskDelete, setSortMenuOpen, bookmarkOverrides, setBookmarkOverrides, onShare }) {
  const navigate = useNavigate();

  const openCourse = (c) => {
    try { sessionStorage.setItem('ui.currentCourseTitle', c.name || ''); } catch { }
    navigate(`/courses/${c.courseId}`);
  };

  if (visibleCourses.length === 0) {
    return (
      <div className="cards-grid">
        <div className="signup-card" style={{ border: '1px solid var(--border-subtle)' }}>
          <p className="hint" style={{ textAlign: 'center' }}>No courses match your filters.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="cards-grid">
      {visibleCourses.map((c) => {
        const eff = (bookmarkOverrides && typeof bookmarkOverrides[c.courseId] === 'boolean')
          ? bookmarkOverrides[c.courseId]
          : !!c.bookmarked;
        return (
          <CourseCard
            key={c.courseId}
            course={c}
            variant="grid"
            bookmarked={eff}
            menuOpen={menuOpenId === c.courseId}
            onMenuOpenChange={(next) => { setSortMenuOpen(false); setMenuOpenId(next ? c.courseId : null); }}
            onOpen={() => openCourse(c)}
            onToggleBookmark={() => {
              const next = !eff;
              setBookmarkOverrides((prev) => ({ ...(prev || {}), [c.courseId]: next }));
              onToggleBookmark(c.courseId, next);
            }}
            onShare={() => { setMenuOpenId(null); onShare(c); }}
            onDelete={() => { setMenuOpenId(null); onAskDelete(c.courseId); }}
          />
        );
      })}
    </div>
  );
}

function PaginationControls({ pagination, loading }) {
  const {
    totalCount,
    totalPages,
    currentPage,
    limit,
    goToPage,
    nextPage,
    prevPage,
    changePageSize,
  } = pagination;

  const [pageSizeMenuOpen, setPageSizeMenuOpen] = useState(false);
  const pageSizeOptions = [6, 12, 24, 48];

  // Calculate visible range
  const startItem = (currentPage - 1) * limit + 1;
  const endItem = Math.min(currentPage * limit, totalCount);

  // Generate page numbers to show
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      // Always show first page
      pages.push(1);

      let start = Math.max(2, currentPage - 1);
      let end = Math.min(totalPages - 1, currentPage + 1);

      // Adjust if we're near the start
      if (currentPage <= 3) {
        end = 4;
      }
      // Adjust if we're near the end
      if (currentPage >= totalPages - 2) {
        start = totalPages - 3;
      }

      if (start > 2) pages.push('...');
      for (let i = start; i <= end; i++) pages.push(i);
      if (end < totalPages - 1) pages.push('...');

      // Always show last page
      pages.push(totalPages);
    }

    return pages;
  };

  useEffect(() => {
    const handleClick = () => setPageSizeMenuOpen(false);
    const handleKey = (e) => { if (e.key === 'Escape') setPageSizeMenuOpen(false); };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, []);

  return (
    <div
      className="pagination-controls"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 16,
        marginTop: 24,
        padding: '16px 0',
        borderTop: '1px solid var(--border-subtle)',
      }}
    >
      {/* Items per page selector */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span className="hint" style={{ fontSize: 13 }}>Show</span>
        <div
          className={`menu-anchor ${pageSizeMenuOpen ? 'open' : ''}`}
          style={{ position: 'relative' }}
        >
          <button
            type="button"
            className="hint"
            onClick={(e) => { e.stopPropagation(); setPageSizeMenuOpen(v => !v); }}
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              padding: '6px 10px',
              background: 'var(--glass-bg-medium)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 8,
              color: 'var(--text-primary)',
              fontSize: 13,
              cursor: 'pointer',
              minWidth: 60,
              justifyContent: 'space-between',
            }}
          >
            <span>{limit}</span>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" style={{ opacity: 0.6 }}>
              <path d="M7 10l5 5 5-5z" />
            </svg>
          </button>
          {pageSizeMenuOpen && (
            <div
              className="menu-card"
              role="menu"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              style={{ minWidth: 60 }}
            >
              {pageSizeOptions.map((size) => (
                <button
                  key={size}
                  role="menuitem"
                  className={`menu-item ${size === limit ? 'is-active' : ''}`}
                  onClick={() => { changePageSize(size); setPageSizeMenuOpen(false); }}
                  style={{ justifyContent: 'center' }}
                >
                  {size}
                </button>
              ))}
            </div>
          )}
        </div>
        <span className="hint" style={{ fontSize: 13 }}>per page</span>
      </div>

      {/* Page info */}
      <span className="hint" style={{ fontSize: 13, flex: '1 1 auto', textAlign: 'center' }}>
        {startItem}–{endItem} of {totalCount}
      </span>

      {/* Page navigation */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <button
          type="button"
          className="pagination-btn"
          onClick={prevPage}
          disabled={currentPage <= 1 || loading}
          aria-label="Previous page"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            background: 'var(--glass-bg-medium)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 8,
            color: 'var(--text-primary)',
            cursor: currentPage <= 1 || loading ? 'not-allowed' : 'pointer',
            opacity: currentPage <= 1 || loading ? 0.4 : 1,
            transition: 'opacity 0.15s ease, background 0.15s ease',
          }}
        >
          <ChevronLeft size={16} />
        </button>

        {getPageNumbers().map((page, idx) => (
          page === '...' ? (
            <span
              key={`ellipsis-${idx}`}
              className="hint"
              style={{ padding: '0 4px', opacity: 0.5 }}
            >
              ...
            </span>
          ) : (
            <button
              key={page}
              type="button"
              className="pagination-btn"
              onClick={() => goToPage(page)}
              disabled={loading}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 32,
                height: 32,
                padding: '0 8px',
                background: page === currentPage ? 'var(--accent-primary)' : 'var(--glass-bg-medium)',
                border: '1px solid',
                borderColor: page === currentPage ? 'var(--accent-primary)' : 'var(--border-subtle)',
                borderRadius: 8,
                color: page === currentPage ? 'var(--text-inverted)' : 'var(--text-primary)',
                fontSize: 13,
                fontWeight: page === currentPage ? 600 : 400,
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'background 0.15s ease, border-color 0.15s ease',
              }}
            >
              {page}
            </button>
          )
        ))}

        <button
          type="button"
          className="pagination-btn"
          onClick={nextPage}
          disabled={currentPage >= totalPages || loading}
          aria-label="Next page"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            background: 'var(--glass-bg-medium)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 8,
            color: 'var(--text-primary)',
            cursor: currentPage >= totalPages || loading ? 'not-allowed' : 'pointer',
            opacity: currentPage >= totalPages || loading ? 0.4 : 1,
            transition: 'opacity 0.15s ease, background 0.15s ease',
          }}
        >
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}